// Tiny typed pub/sub for dev-side observers.
//
// Runtime modules emit through this bus so the debug HUD (timeline,
// inspector, event log, gizmo overlays) can react without the runtime
// importing or knowing about any debug code. Listeners are explicit and
// optional — when no one is listening, an emit is a single set lookup +
// early return.
//
// Event names are namespaced by subsystem: `audio:play`, `state:target`,
// `time:t`, `cloth:grab`, `hero:hover`, `bookmark:saved`, `dev:mode`. The
// `DevEvents` map below gives each event a typed payload — `on()` and
// `emit()` infer it from the event name so callers get autocomplete and
// the payload shape can't drift.
//
// The bus does NOT batch or coalesce. Per-frame events (cloth particle
// updates, mouse moves) should NOT go through the bus — those belong in
// a polled read-current-state pattern. The bus is for state transitions
// (play→stop, hover→select, snap-to-time) that fire a handful of times
// per second at most.

// A cross-surface "focused sound" descriptor. Any audio surface (timeline
// SOUND chip, BEDS row, CUES marker, inspector palette chip, PLAYING-NOW row,
// channel fader, 3D source sphere) emits one of these on hover carrying
// whatever identities it happens to know, and lights itself when an incoming
// focus matches any of them. The fields, widest → narrowest:
//   heroId      the anchored hero (fan, generator, boombox…)
//   placementId a soundscape entry id (bed_… / cue_…)
//   handle      one live playing instance (the AudioManager polyphony key)
//   assetId     the sound asset id (clip)
//   channel     ambient | music | narration | sfx | room
// A focus typically carries several at once (a BED row knows placement +
// hero + asset + channel); a fader carries channel alone. See focusMatches.
export interface FocusRef {
  heroId?: string;
  placementId?: string;
  handle?: string;
  assetId?: string;
  channel?: string;
}

export type DevEvents = {
  // Audio — emitted by AudioManager hooks on play() / stopEntry(). `handle`
  // is the per-instance key, so the same asset sounding from several places
  // is several distinct events.
  'audio:play': { id: string; channel: string; handle: string };
  'audio:stop': { id: string; channel: string; handle: string };
  // A sample buffer finished decoding — its real duration is now known (T3).
  // The timeline corrects a clip's placeholder length once its source decodes.
  'audio:loaded': { id: string; duration: number };
  // Audio cue fired by the time-of-day scheduler (forward crossing of its t).
  'cue:fire': { id: string };
  // A bed population member fired (A12). Read-only viz hook — the timeline can
  // flash the bed's bar; nothing consumes it yet.
  'drop:fire': { placementId: string; asset: string };
  // The generator gate latched on (N8) — fires when the shop powers up.
  'power:on': Record<string, never>;
  // The generator gate latched OFF (item 3) — the shop powers down in the day's
  // closing window. Fires on the on→off flip.
  'power:off': Record<string, never>;
  // Sound palette → CUES brush: the asset "armed" for the next cue drop
  // (null clears). Emitted by the AUDIO-tab palette + the CUES lane picker;
  // consumed by both so the brush stays in sync across the two surfaces.
  'audio:arm': { id: string | null };
  // Cross-surface focus (hover-tier, transient). Emitted by every audio
  // surface on pointer enter/leave; consumed by every other one so a hover
  // anywhere illuminates the same sound everywhere (in its channel color).
  // ref=null clears. This is the hover analogue of hero:select. See FocusRef.
  'audio:focus': { ref: FocusRef | null };
  // State morph — emitted by StateController.setTarget / setProgress.
  'state:target': { target: 'past' | 'present' };
  'state:progress': { progress: number };
  // Time-of-day clock — emitted on programmatic t set + running toggle.
  // (Per-frame tick advances are not emitted; consumers poll `clock.t`.)
  'time:t': { t: number };
  'time:running': { running: boolean };
  // Cloth grab — emitted when a cloth particle is pinned/released via the
  // pointer grab interaction.
  'cloth:grab': { cloth: string; particle: number };
  'cloth:release': { cloth: string };
  // Hero hover/select — emitted by HoverHighlight. id=null means cleared.
  'hero:hover': { id: string | null };
  'hero:select': { id: string | null };
  // Bookmarks — fired by the camera-pose save/tween flows.
  'bookmark:saved': { name: string };
  'bookmark:tween': { name: string };
  // DM13 — a camera/bookmark misconfig surfaced in-UI (event log) instead of console.
  'camera:notice': { message: string };
  // DM14 — observability: the event log narrates the whole authoring session.
  /** A persist result for any aspect (soundscape / manifest / atmosphere). */
  'save:result': { aspect: string; result: 'saved' | 'failed' | 'conflict' };
  /** A gizmo edit committed or stepped through history (3D transform / retag). */
  'gizmo:edit': { action: 'commit' | 'undo' | 'redo' };
  // Director / dev-mode visibility toggle.
  'dev:mode': { on: boolean };
};

type EventName = keyof DevEvents;
type Listener<K extends EventName> = (payload: DevEvents[K]) => void;

class DevBus {
  private listeners: { [K in EventName]?: Set<Listener<K>> } = {};

  on<K extends EventName>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners[event] as Set<Listener<K>> | undefined;
    if (!set) {
      set = new Set<Listener<K>>();
      this.listeners[event] = set as unknown as typeof this.listeners[K];
    }
    set.add(fn);
    return () => set!.delete(fn);
  }

  off<K extends EventName>(event: K, fn: Listener<K>): void {
    const set = this.listeners[event] as Set<Listener<K>> | undefined;
    set?.delete(fn);
  }

  emit<K extends EventName>(event: K, payload: DevEvents[K]): void {
    const set = this.listeners[event] as Set<Listener<K>> | undefined;
    if (!set || set.size === 0) return;
    for (const fn of set) {
      try { fn(payload); }
      catch (e) { console.warn('[devBus] listener error for', event, e); }
    }
  }

  /** Drop every listener. Used by tests / hot-reload. */
  clear(): void {
    this.listeners = {};
  }
}

/** Process-wide singleton. Cheap to import from anywhere; cheap when
 *  nothing's listening. */
export const devBus = new DevBus();

/** Does a focus `ref` light up a `cand`idate element?
 *
 *  An element advertises whatever identities it carries (a BED row knows its
 *  placementId + heroId + assetId + channel; a fader knows only its channel).
 *  We light it when the focus and the candidate share ANY concrete identity —
 *  hero, placement, handle, or asset. Channel matching is the broad case and
 *  only kicks in when at least ONE side carries channel alone (the channel
 *  faders): hovering a fader lights every sound on its channel, and focusing
 *  any specific sound lights its channel's fader — but two specific sounds on
 *  the same channel do NOT spill onto each other. Check concrete ids first. */
export function focusMatches(ref: FocusRef | null | undefined, cand: FocusRef): boolean {
  if (!ref) return false;
  if (ref.heroId && cand.heroId && ref.heroId === cand.heroId) return true;
  if (ref.placementId && cand.placementId && ref.placementId === cand.placementId) return true;
  if (ref.handle && cand.handle && ref.handle === cand.handle) return true;
  if (ref.assetId && cand.assetId && ref.assetId === cand.assetId) return true;
  const refChannelOnly = !ref.heroId && !ref.placementId && !ref.handle && !ref.assetId;
  const candChannelOnly = !cand.heroId && !cand.placementId && !cand.handle && !cand.assetId;
  if ((refChannelOnly || candChannelOnly) && ref.channel && cand.channel && ref.channel === cand.channel) return true;
  return false;
}
